import React, { useEffect, useMemo, useState, ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/pro-light-svg-icons';

import { createStyles } from 'utils/createStyle';
import { Btn } from 'components/btn/Btn';
import { FormBtn } from 'components/form-btn';
import { EASE_OUT_QUART, fadeIn, fadeOut } from 'styles/easing';
import { Radio, RadioOption } from 'components/radio/Radio';
import { OrganizationMembership } from 'common/api/crm/models/OrganizationMembership';
import { removeUserFromOrgThunk } from 'common/features/store/duck/home/duck';
import { useDispatch } from 'react-redux';
import { StatusCode } from 'common/api/config';
import { useAlert } from 'modules/alerts';
import { Modal, ModalSize } from 'components/modal/Modal';
import useMediaQueries from 'hooks/useMediaQueries';
import { Alert, AlertType } from 'components/alerts/sticky/Alert';

const styles = createStyles({
    popover: [
        'border border-solid border-grey-5 bg-white px-4 py-3 duration-200',
        {
            '&[data-state="open"]': { animation: `${fadeIn} 200ms ${EASE_OUT_QUART}` },
            '&[data-state="closed"]': { animation: `${fadeOut} 200ms ${EASE_OUT_QUART}` },
        },
    ],
    container: ['flex flex-col items-top w-full whitespace-normal ', {}],
    back: ['absolute left-0 top-0 ml-1 mt-1 text-xs text-brand'],
    btnCancelLeave: 'w-full text-brand underline font-semibold',
});

const SuccessMessage = ({ orgName }: { orgName: string }) => {
    return (
        <div className="p-4">
            <div className="flex justify-center mb-4">
                <FontAwesomeIcon
                    className="text-success font-semibold text-5xl"
                    icon={faCheckCircle}
                />
            </div>
            <div className="text-base text-center">
                <div className="text-brand-dark text-22px sm:text-29px font-semibold">Success</div>
                <div className="text-lg text-grey-2 flex flex-col items-center mt-5">
                    <p>You have successfully left</p>
                    <p>{orgName}</p>
                </div>
            </div>
        </div>
    );
};

export interface LeaveOrganizationProps {
    defaultOrganization: string;
    memberships: OrganizationMembership[];
    children?: ReactNode;
}

type LeaveOrganizationContentProps = LeaveOrganizationProps & {
    onFinish: () => void;
    confirmedOrganization: string;
    setConfirmedOrganization: (value: string) => void;
    setSuccessfullyLeftOrg: (value: boolean) => void;
};

const LeaveOrganizationContent = ({
    onFinish,
    defaultOrganization,
    memberships,
    confirmedOrganization,
    setConfirmedOrganization,
    setSuccessfullyLeftOrg,
}: LeaveOrganizationContentProps) => {
    const [selectedOrganization, setSelectedOrganization] = useState<RadioOption | null>(null);
    const [leaveOrganization, setLeaveOrganization] = useState('');
    const [isLeavingOrg, setIsLeavingOrg] = useState<boolean>(false);
    const dispatch = useDispatch<any>();
    const alert = useAlert();

    const options = useMemo(() => {
        return memberships.reduce((acc: RadioOption[], m: OrganizationMembership) => {
            const isDefaultOrganization = m.organization.id === defaultOrganization;
            if (isDefaultOrganization) {
                // special case push to head of the list
                return [
                    {
                        text: m.organization.name + ' - Default',
                        value: m.organization.id,
                        disabled: true,
                    },
                    ...acc,
                ];
            } else {
                acc.push({
                    text: m.organization.name,
                    value: m.organization.id,
                    disabled: !m.isActive,
                } as RadioOption);
            }
            return acc;
        }, []);
    }, [memberships, defaultOrganization]);

    const cancel = () => {
        onFinish();
    };

    const confirmLeave = async () => {
        setIsLeavingOrg(true);
        const response = await removeUserFromOrgThunk(confirmedOrganization)(dispatch);
        if (response.response?.status !== StatusCode.Ok) {
            alert({ message: 'Unable to leave organization try again later.' });
        } else {
            setLeaveOrganization(confirmedOrganization);
            setSuccessfullyLeftOrg(true);
        }
        setIsLeavingOrg(false);
    };

    useEffect(() => {
        if (confirmedOrganization && leaveOrganization) {
            const timeout = setTimeout(() => onFinish(), 3000);
            return () => clearTimeout(timeout);
        }
    }, [confirmedOrganization, leaveOrganization, onFinish]);

    return (
        <div className={styles.container}>
            {!leaveOrganization && <Title />}
            {/* STEP 1: Choose Organization */}
            {!leaveOrganization && (
                <div>
                    <div className="px-1">
                        <Radio
                            options={options}
                            value={selectedOrganization?.value ?? ''}
                            onChange={(opt) => setSelectedOrganization(opt)}
                            extraStyles="text-sm text-grey-1 font-normal"
                        />
                        {!confirmedOrganization && (
                            <div className="text-center md:text-right mt-12">
                                <Btn
                                    disabled={!selectedOrganization}
                                    type="button"
                                    className="mx-auto block w-32 md:w-auto max-w-xs md:px-8 md:py-1 mb-5"
                                    onClick={() =>
                                        setConfirmedOrganization(selectedOrganization?.value ?? '')
                                    }
                                >
                                    Leave
                                </Btn>
                                <button
                                    type="button"
                                    className={styles.btnCancelLeave}
                                    onClick={cancel}
                                >
                                    Cancel
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* STEP 2: Confirm leave from Organization */}
            {confirmedOrganization && !leaveOrganization && (
                <DeletionConfirmationAlert
                    isSubmitting={isLeavingOrg}
                    orgName={selectedOrganization?.text ?? ''}
                    onConfirm={confirmLeave}
                    cancel={cancel}
                />
            )}
            {/* STEP 3: Leave Organization Successfully */}
            {confirmedOrganization && leaveOrganization && (
                <div>
                    <SuccessMessage orgName={selectedOrganization?.text ?? ''} />
                </div>
            )}
        </div>
    );
};

const DeletionConfirmationAlert = ({
    orgName,
    onConfirm,
    cancel,
    isSubmitting,
}: {
    orgName: string;
    onConfirm: () => void;
    cancel: () => void;
    isSubmitting: boolean;
}) => {
    return (
        <div className="mt-8">
            <Alert type={AlertType.ERROR} title={`Are you sure you want to leave ${orgName}`}>
                <p className="text-grey-2 text-xs">
                    Order history with this organization will still be available but you’ll no
                    longer be associated or able to apply earnings.
                </p>
                <div className="mx-auto w-max sm:w-auto flex items-center flex-col-reverse gap-5 sm:flex-row mt-5 sm:ml-14">
                    <button
                        disabled={isSubmitting}
                        className="text-lg text-brand font-semibold underline"
                        onClick={cancel}
                    >
                        No, Cancel
                    </button>
                    <FormBtn isSubmitting={isSubmitting} onClick={onConfirm}>
                        Yes, Leave
                    </FormBtn>
                </div>
            </Alert>
        </div>
    );
};

export const LeaveOrganization = ({ defaultOrganization, memberships }: LeaveOrganizationProps) => {
    const [open, setOpen] = useState(false);
    const [confirmedOrganization, setConfirmedOrganization] = useState('');
    const [successfullyLeftOrg, setSuccessfullyLeftOrg] = useState<boolean>(false);

    const reset = () => {
        setOpen(false);
        setConfirmedOrganization('');
        setSuccessfullyLeftOrg(false);
    };

    const onFinish = () => {
        reset();
    };

    return (
        <div className="inline-block">
            <div>
                <p
                    className="leave_org_cta text-brand text-xs underline font-semibold cursor-pointer"
                    onClick={() => setOpen(true)}
                >
                    Leave Organization
                </p>
                <Modal
                    title=""
                    isOpen={open}
                    onClose={() => reset()}
                    showCloseButton={!successfullyLeftOrg}
                    size={[
                        ModalSize.EXTRA_SMALL,
                        successfullyLeftOrg ? ModalSize.SMALL : ModalSize.MEDIUM,
                    ]}
                >
                    <LeaveOrganizationContent
                        onFinish={onFinish}
                        defaultOrganization={defaultOrganization}
                        memberships={memberships}
                        confirmedOrganization={confirmedOrganization}
                        setConfirmedOrganization={setConfirmedOrganization}
                        setSuccessfullyLeftOrg={setSuccessfullyLeftOrg}
                    />
                </Modal>
            </div>
        </div>
    );
};

const Title = () => {
    const classes = useStyles();
    return (
        <div className={classes.container}>
            <h1 className={classes.title}>Select an organization to leave</h1>
            <h4 className={classes.subTitle}>You cannot leave your default organization</h4>
        </div>
    );
};

const useStyles = () => {
    const [styles, setStyles] = useState(
        createStyles({
            container: ['text-center flex gap-2  flex-col mb-3'],
            title: ['font-semibold text-brand-dark', { fontSize: 29 }],
            subTitle: ['text-grey-2'],
        })
    );

    const query = useMemo(
        () => ({
            '(max-width: 648px)': () => {
                setStyles(
                    createStyles({
                        container: ['text-center flex gap-2  flex-col mb-3'],
                        title: ['font-semibold text-brand-dark', { fontSize: 22 }],
                        subTitle: ['text-grey-2', { fontSize: 16, lineHeight: '22px' }],
                    })
                );
            },
            '(min-width: 647px)': () => {
                setStyles(
                    createStyles({
                        container: ['text-center flex gap-2  flex-col mb-3'],
                        title: ['font-semibold text-brand-dark', { fontSize: 29 }],
                        subTitle: ['text-grey-2', { fontSize: 17, lineHeight: '24px' }],
                    })
                );
            },
        }),
        []
    );

    useMediaQueries(query);
    return styles;
};
